import React from 'react';
import { Badge, tokens } from '@fluentui/react-components';

// Mirrors OrderStatusPill in shape so the two read as a matched pair when
// rendered side-by-side in the order header. Booqable's payment_status
// values map to staff-facing labels here.
export type PaymentPill = {
  label: string;
  background: string;
  foreground: string;
  border?: string;
  /** Saturated hue for the inline dot when the signal color is the fill (and
   *  the foreground is dark/neutral text rather than the saturated color). */
  accent?: string;
};

// Status → label/colors map, shared with PaymentStatusBanner so the pill (order
// header) and the banner (payments section) read as the same status language.
export const PAYMENT_PILLS: Record<string, PaymentPill> = {
  paid:           { label: 'Paid',           background: '#d3f3e9', foreground: '#107c41' },
  payment_paid:   { label: 'Paid',           background: '#d3f3e9', foreground: '#107c41' },
  partially_paid: { label: 'Partially Paid', background: '#cfe4ff', foreground: '#0078d4' },
  payment_due:    { label: 'Payment Due',    background: 'rgb(255, 180, 66)', foreground: tokens.colorNeutralForeground1, accent: 'rgb(255, 180, 66)' },
  overpaid:       { label: 'Overpaid',       background: '#fde7e9', foreground: '#d13438' },
};

const PILL_STYLE: React.CSSProperties = {
  paddingTop: '4px',
  paddingBottom: '4px',
  paddingLeft: '12px',
  paddingRight: '12px',
  height: 'auto',
  width: '120px',
  justifyContent: 'center',
  textAlign: 'center',
};

interface PaymentStatusPillProps {
  status: string | null | undefined;
  /** Optional leading icon rendered inside the pill (e.g. a dimension glyph
   *  in the order header). Omitted in table contexts. */
  icon?: React.ReactNode;
  /** Render a compact colored dot + label instead of a filled pill — for
   *  placement next to a total (e.g. the project phase card footer). */
  inline?: boolean;
}

const PaymentStatusPill: React.FC<PaymentStatusPillProps> = ({ status, icon, inline }) => {
  if (!status) return <>—</>;
  const key = status.toLowerCase();
  const pill = PAYMENT_PILLS[key];
  // Dot + label: the saturated status hue carries the signal, the label stays
  // quiet neutral text so it sits calmly beside the total. (For payment_due the
  // saturated color is the fill, so an explicit `accent` supplies the dot hue.)
  if (inline) {
    const accent = !pill
      ? tokens.colorNeutralForeground3
      : pill.accent
        ? pill.accent
        : pill.foreground;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: accent,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 }}>
          {pill?.label ?? status}
        </span>
      </span>
    );
  }
  if (!pill) {
    return (
      <Badge
        icon={icon}
        appearance="filled"
        style={{
          ...PILL_STYLE,
          backgroundColor: tokens.colorNeutralBackground3,
          color: tokens.colorNeutralForeground1,
        }}
      >
        {status}
      </Badge>
    );
  }
  return (
    <Badge
      icon={icon}
      appearance="filled"
      style={{
        ...PILL_STYLE,
        backgroundColor: pill.background,
        color: pill.foreground,
      }}
    >
      {pill.label}
    </Badge>
  );
};

export default PaymentStatusPill;
